/**
 * 🔴 **THE DEVELOPMENT PERSONA PICKER'S DATA** (D53).
 *
 * Devin: *"in the dev environment on the sign in page let me just select the person to sign in and
 * display their role so I can see and test their view easily."*
 *
 * 🔴 **Its absence is the OFF SWITCH, and that is the whole design.** The endpoint answers 404
 * anywhere but development (`services/auth/dev-people.ts` — two gates, secret and environment type),
 * so a 404 here is not an error to report: it is a deployed environment saying there is no such
 * thing. The picker simply never renders, and the card is an address and a password like everybody
 * else's. No flag in the shell to get wrong, and no build that ships a staff directory by accident.
 */
import { API_BASE_URL } from "@/api/base";
import type { FetchLike } from "./http";

export const DEV_PEOPLE_PATH = "/auth/dev-people";
/**
 * How long one ask waits. 🔴 NOT short (round 3): at 2.5 s the first ask of a cold smoke backend (2.6 s)
 * was dropped, the picker never appeared, and a tester who must not type passwords was locked out until
 * a reload. The card never waits on this — it renders at once and the picker joins it — so a long wait
 * costs nothing, while a short one hides the only way in.
 */
export const DEV_PEOPLE_TIMEOUT_MS = 10_000;
/** A timed-out or unreachable ask is made once more (a backend still waking up); a 404 is never retried. */
export const DEV_PEOPLE_ATTEMPTS = 2;

export interface DevPerson {
  email: string;
  displayName: string;
  platform: string;
  roles: string;
  canSignIn: boolean;
  /**
   * 🔴 What this persona is FOR — "Platform admin", "Agent", "Producer" — the reason to pick this
   * row. The server curates the roster to a handful (`DEV_PERSONAS`); this is what each is for.
   * Optional because an older server does not send it, and a picker with no purposes still works.
   */
  purpose?: string;
  /** False when the address is on the roster and not on this database — say so, never hide the row. */
  seeded?: boolean;
}

interface Body {
  people?: unknown;
}

const isPerson = (value: unknown): value is DevPerson => {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Partial<DevPerson>;
  return typeof row.email === "string" && typeof row.displayName === "string";
};

/** The roster, or `[]` for every failure — including the 404 that means "not a development build". */
export async function fetchDevPeople(base: string = API_BASE_URL, fetchImpl?: FetchLike): Promise<DevPerson[]> {
  const send = fetchImpl ?? fetch;
  for (let attempt = 1; attempt <= DEV_PEOPLE_ATTEMPTS; attempt += 1) {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), DEV_PEOPLE_TIMEOUT_MS);
    let response: Response;
    try {
      response = await send(`${base}${DEV_PEOPLE_PATH}`, { signal: abort.signal });
    } catch {
      clearTimeout(timer);
      continue; // Unreachable or too slow: a backend waking up gets one more ask.
    }
    try {
      // An ANSWER is final: a 404 is the off switch, and anything unreadable is no roster.
      if (!response.ok) return [];
      const body = (await response.json()) as Body;
      return Array.isArray(body.people) ? body.people.filter(isPerson) : [];
    } catch {
      return [];
    } finally {
      clearTimeout(timer);
    }
  }
  return [];
}

/**
 * 🔴 The password every development account is seeded with (`tools/seed-dev-passwords.mts`).
 *
 * Written here in the open rather than fetched, because a "give me the password" endpoint is a far
 * worse thing to own than a constant in a picker that only exists when the server has already said
 * this is a development build. The picker fills the REAL form with it and submits the REAL sign-in —
 * so what is being tested is the path everybody uses, not a bypass beside it.
 */
// 🔴 Re-exported from `@pinksy/contracts`, NOT written out again. This constant and the one in
// `tools/seed-dev-passwords.mts` are the two ends of one handshake — the picker types what the
// seeder seeded — and they were allowed to drift: the seeder moved to "pinksy-dev-password" while
// this file still said "password", so the picker signed nobody in. A shared constant makes the two
// ends the same fact rather than two facts that have to be kept equal by hand.
export { DEV_PASSWORD } from "@pinksy/contracts";
